'use client';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

/**
 * The scroll container a long facet's options sit in.
 *
 * It renders a JS scrollbar rather than a native one because the requirement is
 * a bar that never auto-hides, on every browser. WebKit and Blink can be forced
 * to keep one with `::-webkit-scrollbar` — that is what `@vs-shop/ui`'s
 * `scrollbar-stable` utility does — but Firefox ignores it and obeys the
 * reader's own overlay-scrollbar setting instead. A facet whose bar appears only
 * mid-scroll gives no indication that there are forty more options below the ten
 * in view, which is the whole reason the container exists.
 *
 * That is a rendering requirement, not a library preference — replacing this
 * with a headless scroll area means first showing that the replacement keeps an
 * always-visible bar in Firefox.
 *
 * The height cap is what makes the rail scrollable at all: without it the
 * container grows to its content and the page scrolls instead, which puts the
 * remaining facets below the fold.
 *
 * **Every app that renders this part must import the library's stylesheet**
 * (`@import 'overlayscrollbars/overlayscrollbars.css'`) in its Tailwind entry,
 * and in the Storybook preview of any tenant whose instance shows it. The
 * stylesheet is what clips the box and paints the bar; a package cannot import
 * it on an app's behalf, and rule 19's coverage guard cannot see it because it
 * lives outside the workspace. Without it this container does not clip at all —
 * the options overflow and the page scrolls, with nothing failing anywhere.
 */
const filterRailScrollerClasses = 'tab-lg:max-h-[300px] max-h-[250px] pe-3';

export type FilterRailScrollerProps = ComponentProps<typeof OverlayScrollbarsComponent>;

/** The options' scroll container. */
export const FilterRailScroller = ({ className, ...props }: FilterRailScrollerProps) => (
  <OverlayScrollbarsComponent
    element="div"
    options={{
      scrollbars: { visibility: 'auto', autoHide: 'never' },
      overflow: { x: 'hidden', y: 'scroll' },
    }}
    defer
    data-slot="filter-rail-scroller"
    className={clsxm(filterRailScrollerClasses, className)}
    {...props}
  />
);
